"use client";

import { useEffect, useRef, useState } from "react";
import { usePathname, useRouter } from "next/navigation";

import { getToken, isUnauthorized } from "@/utils/auth";
import { useAuthSessionSync } from "@/lib/use-auth-token-sync";
import { apiRequest } from "@/lib/api";
import { User } from "@/lib/types";
import { LoadFailed } from "@/components/auth/load-failed";
import AppShell from "@/components/AppShell";
import { AuthContext } from "@/components/RequireAuth";
import { canAccessPortal, type Portal } from "@/lib/portals";

const PUBLIC_PREFIXES = [
  "/login",
  "/logout",
  "/register",
  "/forgot-password",
  "/reset-password",
  "/verify-email",
  "/docs",
];

// Authenticated, but rendered outside the staff AppShell/Sidebar — these
// routes supply their own chrome (the portal-picker splash and each
// non-Staff portal's own shell). Staff itself stays the AppShell fallback
// for everything not listed here.
const NO_APPSHELL_PREFIXES = [
  "/select-portal",
  "/provider",
  "/pharmacist",
  "/customer-success",
  "/sales",
  "/patient",
];

export default function AuthShell({ children }: { children: React.ReactNode }) {
  const pathname = usePathname();
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [isChecking, setIsChecking] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const checkedRef = useRef(false);
  const [reloadKey, setReloadKey] = useState(0);

  const isPublic = PUBLIC_PREFIXES.some((p) => pathname === p || pathname?.startsWith(p + "/"));
  const isNoAppShell = NO_APPSHELL_PREFIXES.some((p) => pathname === p || pathname?.startsWith(p + "/"));

  useEffect(() => {
    if (isPublic) {
      // Reset when landing on a public page (e.g. after logout)
      checkedRef.current = false;
      setUser(null);
      setIsChecking(false);
      return;
    }

    if (checkedRef.current) return;
    checkedRef.current = true;

    setIsChecking(true);
    setLoadFailed(false);
    let cancelled = false;

    const checkAuth = async (retriedStale = false): Promise<void> => {
      if (!getToken()) {
        router.push("/login");
        return;
      }
      try {
        const data = await apiRequest("get", "/me/");
        if (!cancelled) {
          setUser(data as User);
          setIsChecking(false);
        }
      } catch (err) {
        if (cancelled) return;
        if (isUnauthorized(err)) {
          // lib/api.ts already settled/cleared the token; if another tab put a
          // new one in meanwhile, try once with it.
          if (getToken() && !retriedStale) return checkAuth(true);
          router.push("/login");
          return;
        }
        // Network error / 5xx: keep the session, let the user retry.
        setLoadFailed(true);
        setIsChecking(false);
      }
    };

    checkAuth();
    return () => {
      cancelled = true;
    };
  }, [isPublic, router, reloadKey]);

  useAuthSessionSync({
    user,
    onUser: (data) => {
      setUser(data);
      setLoadFailed(false);
    },
    onLogout: () => router.push("/login"),
    enabled: !isPublic,
  });

  if (isPublic) return <>{children}</>;
  if (isChecking) return null;
  if (loadFailed) {
    return (
      <LoadFailed
        onRetry={() => {
          checkedRef.current = false;
          setReloadKey((k) => k + 1);
        }}
      />
    );
  }

  if (isNoAppShell) {
    return <AuthContext.Provider value={user}>{children}</AuthContext.Provider>;
  }

  const currentPortal: Portal = pathname.startsWith("/staff/engineering") && canAccessPortal(user, "engineering")
    ? "engineering"
    : "staff";

  const navSections = currentPortal === "engineering"
    ? [
        {
          section: "Engineering",
          links: [
            {
              label: "Recommendations",
              href: "/staff/engineering/recommendations",
              icon: "wrench",
            },
          ],
        },
      ]
    : user?.is_super_admin
    ? [
        ...(user.navigation ?? []),
        {
          section: "Administration",
          links: [
            {
              label: "Users",
              href: "/staff/users",
              icon: "users",
            },
          ],
        },
      ]
    : user?.navigation ?? [];

  return (
    <AuthContext.Provider value={user}>
      <AppShell
        navSections={navSections}
        currentPortal={currentPortal}
        userName={user ? `${user.first_name} ${user.last_name}`.trim() : undefined}
      >
        {children}
      </AppShell>
    </AuthContext.Provider>
  );
}
